import { IconType } from 'react-icons';
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';
import { AiRecommendation } from '../types';

/**
 * Shared formatting, styling and configuration helpers for the AI credit
 * analysis feature. Single source of truth consumed by AiAnalysisModal,
 * AiRationaleModal and the analysis table columns.
 */

// ── Thresholds ──

const PDEFAULT_HIGH = 0.15;
const PDEFAULT_MEDIUM = 0.05;

// ── Formatting ──

/** Format the AI-estimated probability of default (0.0–1.0) as a percentage. Returns '—' when absent. */
export const formatPdefault = (pdefault: number | null | undefined): string => {
    if (pdefault === null || pdefault === undefined) return '—';
    return `${(pdefault * 100).toFixed(1)}%`;
};

/** Format the AI confidence (0.0–1.0) as an integer percentage. Returns '—' when absent. */
export const formatConfidence = (confidence: number | null | undefined): string => {
    if (confidence === null || confidence === undefined) return '—';
    return `${(confidence * 100).toFixed(0)}%`;
};

/** Format a processing duration in milliseconds as a human-readable string. */
export const formatProcessingTime = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
};

// ── Styling ──

/** Tailwind text-color class for a pdefault value (green < 5%, amber 5–15%, red ≥ 15%, gray when absent). */
export const getPdefaultColorClass = (pdefault: number | null | undefined): string => {
    if (pdefault === null || pdefault === undefined) return 'text-gray-400 dark:text-gray-500';
    if (pdefault >= PDEFAULT_HIGH) return 'text-red-600 dark:text-red-400';
    if (pdefault >= PDEFAULT_MEDIUM) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
};

// ── Recommendation config ──

interface RecommendationConfig {
    label: string;
    badgeClass: string;
    icon: IconType;
}

/** Single mapping of recommendation → label, badge style and icon. */
export const AI_RECOMMENDATION_CONFIG: Record<AiRecommendation, RecommendationConfig> = {
    [AiRecommendation.APPROVE]: { label: 'Aprovar', badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: FaCheckCircle },
    [AiRecommendation.REJECT]: { label: 'Reprovar', badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: FaTimesCircle },
    [AiRecommendation.REVIEW]: { label: 'Revisar', badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: FaExclamationTriangle },
};

/** Get the config for a recommendation, falling back to REVIEW for unknown/absent values. */
export const getRecommendationConfig = (recommendation: AiRecommendation | null | undefined): RecommendationConfig =>
    (recommendation && AI_RECOMMENDATION_CONFIG[recommendation]) || AI_RECOMMENDATION_CONFIG[AiRecommendation.REVIEW];
